import React, { createContext, useCallback, useMemo, useState } from 'react';

export type ZIndexLayerId =
  | 'map-sidebar'
  | 'map-attributes-table'
  | 'map-filters-modal'
  | 'map-subtitles-modal'
  | 'map-informations-modal'
  | 'map-features-modal'
  | 'map-analytes-modal'
  | 'map-details-modal';

interface IZIndexContext {
  getZIndex: (id: ZIndexLayerId) => number;
  bringToFront: (id: ZIndexLayerId) => void;
}

const BASE_Z_INDEX = 2;
const DEFAULT_STACK: ZIndexLayerId[] = [
  'map-sidebar',
  'map-attributes-table',
  'map-filters-modal',
  'map-subtitles-modal',
  'map-informations-modal',
  'map-features-modal',
  'map-analytes-modal',
  'map-details-modal',
];

export const ZIndexContext = createContext<IZIndexContext>({} as IZIndexContext);

interface IProvider {
  children: React.ReactNode;
}

export function ZIndexProvider({ children }: IProvider) {
  const [stack, setStack] = useState<ZIndexLayerId[]>(DEFAULT_STACK);

  const bringToFront = useCallback((id: ZIndexLayerId) => {
    setStack(prev => {
      const next = prev.filter(item => item !== id);
      return [...next, id];
    });
  }, []);

  const getZIndex = useCallback(
    (id: ZIndexLayerId) => {
      const index = stack.indexOf(id);
      const safeIndex = index === -1 ? stack.length : index;
      return BASE_Z_INDEX + safeIndex;
    },
    [stack],
  );

  const value = useMemo(
    () => ({
      getZIndex,
      bringToFront,
    }),
    [getZIndex, bringToFront],
  );

  return <ZIndexContext.Provider value={value}>{children}</ZIndexContext.Provider>;
}
